import { router, usePage } from '@inertiajs/react'
import { Download } from 'lucide-react'
import { t } from '@/shared/lib/i18n'
import { useBreadcrumbs } from '@/shared/lib/breadcrumbs-context'
import {
  complianceDashboardPath,
  complianceCsvExportPath,
  compliancePdfExportReportPath,
} from '@/shared/lib/routes'
import { Button } from '@/shared/ui/button'
import ComplianceFilters from './components/ComplianceFilters'
import ComplianceTable from './components/ComplianceTable'
import DataPagination from '@/shared/ui/DataPagination'
import type { PaginationMeta } from '@/shared/types/PaginationMeta'
import type { WebComplianceReviewList } from '@/shared/types/web'
import type { SharedProps } from '@/shared/types/SharedProps'
import { downloadExport } from '@/shared/lib/downloadExport'

interface InstrumentOption {
  value: number
  label: string
}

interface SiteOption {
  value: number
  label: string
}

interface DepartmentOption {
  value: number
  label: string
  siteId?: number
}

interface UnitOption {
  value: number
  label: string
  siteId?: number
  departmentId?: number
}

interface ComplianceFiltersShape {
  siteIds?: number[]
  departmentIds?: number[]
  unitIds?: number[]
  instrumentIds?: string
  dateFrom?: string
  dateTo?: string
  value?: string
  reviewStatusId?: string
  resolutionTypeCode?: string
  search?: string
}

interface ReviewStatusOption {
  id: number
  label: string
}

interface Props {
  records: WebComplianceReviewList[]
  meta: PaginationMeta
  filters: ComplianceFiltersShape
  reviewStatuses: ReviewStatusOption[]
  instrumentOptions: InstrumentOption[]
  siteOptions: SiteOption[]
  departmentOptions: DepartmentOption[]
  unitOptions: UnitOption[]
  [key: string]: unknown
}

const ComplianceDashboardPage = () => {
  const {
    records,
    meta,
    filters,
    reviewStatuses,
    instrumentOptions,
    siteOptions,
    departmentOptions,
    unitOptions,
    tenantTimeFormat,
  } = usePage<Props & SharedProps>().props

  useBreadcrumbs([{ label: t('nav.complianceReviews') }])

  const goToPage = (page: number) => {
    const params = new URLSearchParams(window.location.search)
    params.set('page', String(page))
    router.visit(`${complianceDashboardPath()}?${params.toString()}`, { preserveState: true })
  }

  const handleExportCsv = () => {
    const params = new URLSearchParams(window.location.search)
    params.delete('page')
    const queryString = params.toString()
    void downloadExport(
      `${complianceCsvExportPath()}${queryString ? `?${queryString}` : ''}`,
      'export.csv',
    )
  }

  const handleExportPdf = () => {
    const params = new URLSearchParams(window.location.search)
    params.delete('page')
    const queryString = params.toString()
    void downloadExport(
      `${compliancePdfExportReportPath()}${queryString ? `?${queryString}` : ''}`,
      'export.pdf',
    )
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="flex items-center justify-between gap-3">
        <div className="space-y-1">
          <h1 className="text-xl font-bold sm:text-2xl">{t('nav.complianceReviews')}</h1>
          <p className="max-w-3xl text-sm text-muted-foreground">
            {t('compliance.dashboard.pendingListHelp')}
          </p>
          <p className="max-w-3xl text-sm text-muted-foreground">
            {t('compliance.dashboard.decisionHelp')}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" className="gap-2" onClick={handleExportCsv}>
            <Download className="h-4 w-4" />
            {t('compliance.dashboard.exportCsv')}
          </Button>
          <Button variant="outline" size="sm" className="gap-2" onClick={handleExportPdf}>
            <Download className="h-4 w-4" />
            {t('compliance.dashboard.exportPdf')}
          </Button>
          <div className="lg:hidden">
            <ComplianceFilters
              filters={filters}
              reviewStatuses={reviewStatuses}
              instrumentOptions={instrumentOptions}
              siteOptions={siteOptions}
              departmentOptions={departmentOptions}
              unitOptions={unitOptions}
              triggerOnly
            />
          </div>
        </div>
      </div>

      {/* Desktop inline filters */}
      <ComplianceFilters
        filters={filters}
        reviewStatuses={reviewStatuses}
        instrumentOptions={instrumentOptions}
        siteOptions={siteOptions}
        departmentOptions={departmentOptions}
        unitOptions={unitOptions}
        desktopOnly
      />

      <ComplianceTable records={records} tenantTimeFormat={tenantTimeFormat} />

      <DataPagination
        meta={meta}
        onPageChange={goToPage}
        noun={t('compliance.dashboard.paginationRecords')}
      />
    </div>
  )
}

export default ComplianceDashboardPage
